import type React from "react";
import { useState, useRef } from "react";
import { Check } from "lucide-react";
import { cn } from "@hipotecar/ui/lib/utils";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";

interface Option {
	value: string;
	text: string;
	shortText?: string;
}

interface MultiSelectProps {
	label: string;
	options: Option[];
	defaultSelected?: string[];
	value?: string[];
	onChange?: (selected: string[]) => void;
	disabled?: boolean;
	placeholder?: string;
}

const MultiSelect: React.FC<MultiSelectProps> = ({
	label,
	options,
	defaultSelected = [],
	value,
	onChange,
	disabled = false,
	placeholder = "Select options",
}) => {
	const isControlled = value !== undefined;
	const [internalSelected, setInternalSelected] =
		useState<string[]>(defaultSelected);
	const selectedOptions = isControlled ? value : internalSelected;
	const [isOpen, setIsOpen] = useState(false);
	const [focusedIndex, setFocusedIndex] = useState(-1);
	const dropdownRef = useRef<HTMLDivElement>(null);

	const updateSelection = (newSelected: string[]) => {
		if (!isControlled) setInternalSelected(newSelected);
		onChange?.(newSelected);
	};

	const toggleDropdown = () => {
		if (!disabled) {
			setIsOpen((prev) => !prev);
			setFocusedIndex(-1);
		}
	};

	const handleSelect = (optionValue: string) => {
		if (disabled) return;
		const newSelected = selectedOptions.includes(optionValue)
			? selectedOptions.filter((v) => v !== optionValue)
			: [...selectedOptions, optionValue];
		updateSelection(newSelected);
	};

	const removeOption = (optionValue: string) => {
		updateSelection(selectedOptions.filter((v) => v !== optionValue));
	};

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (disabled) return;

		e.preventDefault();
		switch (e.key) {
			case "Enter":
				if (!isOpen) {
					setIsOpen(true);
				} else if (focusedIndex >= 0) {
					handleSelect(options[focusedIndex].value);
				}
				break;
			case "Escape":
				setIsOpen(false);
				break;
			case "ArrowDown":
				if (!isOpen) {
					setIsOpen(true);
				} else {
					setFocusedIndex((prev) => (prev < options.length - 1 ? prev + 1 : 0));
				}
				break;
			case "ArrowUp":
				if (isOpen) {
					setFocusedIndex((prev) => (prev > 0 ? prev - 1 : options.length - 1));
				}
				break;
		}
	};

	return (
		<div className="w-full" ref={dropdownRef}>
			{label && (
				<label className="label-text" id={`${label}-label`}>
					{label}
				</label>
			)}

			<div className="relative z-20 inline-block w-full">
				{/*
				  Radix's PopoverTrigger opens the popover from its own click
				  handler, which runs whether or not the wrapper below guards on
				  `disabled` — it only honours a `disabled` attribute on a real
				  button. Gating both the open state and the change handler here
				  is what actually keeps a disabled MultiSelect shut.
				*/}
				<Popover
					open={isOpen && !disabled}
					onOpenChange={(next) => {
						if (disabled) return;
						setIsOpen(next);
					}}
				>
					<PopoverTrigger asChild>
						<div
							onClick={toggleDropdown}
							onKeyDown={handleKeyDown}
							className="w-full"
							role="combobox"
							aria-expanded={isOpen}
							aria-haspopup="listbox"
							aria-labelledby={`${label}-label`}
							aria-disabled={disabled}
							tabIndex={disabled ? -1 : 0}
						>
							<div
								className={cn(
									"mb-2 flex min-h-11 rounded-lg border px-3 py-1.5 shadow-theme-xs transition-colors dark:bg-gray-900",
									"focus:border-brand-300 focus:ring-3 focus:ring-brand-500/20 focus:outline-none dark:focus:border-brand-800",
									isOpen
										? "border-brand-300 ring-brand-500/20 dark:border-brand-800 ring-3"
										: "border-default dark:border-gray-700",
									disabled
										? "cursor-not-allowed bg-gray-50 opacity-50 dark:bg-gray-800"
										: "cursor-pointer",
								)}
							>
								<div className="flex flex-auto flex-wrap gap-2">
									{selectedOptions.length > 0 ? (
										selectedOptions.map((value) => {
											const option = options.find((opt) => opt.value === value);
											const text = option?.shortText || option?.text || value;
											return (
												<div
													key={value}
													className="group flex items-center justify-center rounded-full border border-gray-200 bg-transparent py-1 pr-2 pl-2.5 text-sm text-gray-700 hover:border-gray-300 dark:border-slate-700 dark:bg-transparent dark:text-slate-400 dark:hover:border-slate-600"
												>
													<span className="max-w-full flex-initial">
														{text}
													</span>
													<button
														type="button"
														onClick={(e) => {
															e.stopPropagation();
															if (!disabled) removeOption(value);
														}}
														disabled={disabled}
														className="cursor-pointer pl-2 text-gray-500 group-hover:text-gray-900 disabled:cursor-not-allowed dark:text-gray-400 dark:group-hover:text-gray-200"
														aria-label={`Remove ${text}`}
													>
														<svg
															className="fill-current"
															width="14"
															height="14"
															viewBox="0 0 14 14"
															xmlns="http://www.w3.org/2000/svg"
														>
															<path
																fillRule="evenodd"
																clipRule="evenodd"
																d="M3.40717 4.46881C3.11428 4.17591 3.11428 3.70104 3.40717 3.40815C3.70006 3.11525 4.17494 3.11525 4.46783 3.40815L6.99943 5.93975L9.53095 3.40822C9.82385 3.11533 10.2987 3.11533 10.5916 3.40822C10.8845 3.70112 10.8845 4.17599 10.5916 4.46888L8.06009 7.00041L10.5916 9.53193C10.8845 9.82482 10.8845 10.2997 10.5916 10.5926C10.2987 10.8855 9.82385 10.8855 9.53095 10.5926L6.99943 8.06107L4.46783 10.5927C4.17494 10.8856 3.70006 10.8856 3.40717 10.5927C3.11428 10.2998 3.11428 9.8249 3.40717 9.53201L5.93877 7.00041L3.40717 4.46881Z"
															/>
														</svg>
													</button>
												</div>
											);
										})
									) : (
										<div className="pointer-events-none size-full p-1 pr-2 text-sm text-tertiary">
											{placeholder}
										</div>
									)}
								</div>
								<div className="flex w-7 items-center self-start p-1">
									<button
										type="button"
										onClick={(e) => {
											e.stopPropagation();
											toggleDropdown();
										}}
										disabled={disabled}
										className="size-5 cursor-pointer text-gray-500 outline-hidden hover:text-gray-900 focus:outline-hidden disabled:cursor-not-allowed dark:text-gray-400 dark:hover:text-gray-200"
									>
										<svg
											className={cn(
												"stroke-current transition-transform",
												isOpen && "rotate-180",
											)}
											width="20"
											height="20"
											viewBox="0 0 20 20"
											fill="none"
											xmlns="http://www.w3.org/2000/svg"
										>
											<path
												d="M4.79175 7.39551L10.0001 12.6038L15.2084 7.39551"
												stroke="currentColor"
												strokeWidth="1.5"
												strokeLinecap="round"
												strokeLinejoin="round"
											/>
										</svg>
									</button>
								</div>
							</div>
						</div>
					</PopoverTrigger>

					<PopoverContent
						className={cn(
							"z-100 custom-scrollbar max-h-60 w-(--radix-popover-trigger-width) overflow-y-auto rounded-lg border border-default bg-card p-1 text-primary shadow-theme-xl",
							"dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300",
						)}
						align="start"
						sideOffset={4}
						onOpenAutoFocus={(e) => e.preventDefault()}
					>
						{options.map((option, index) => {
							const isSelected = selectedOptions.includes(option.value);
							const isFocused = index === focusedIndex;

							return (
								<div
									key={option.value}
									className={cn(
										"relative flex w-full cursor-default items-center rounded-md py-2 pr-2 pl-8 text-sm transition-colors outline-none select-none",
										"hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-800 dark:hover:text-gray-200",
										isFocused &&
											"bg-gray-100 text-gray-900 dark:bg-gray-800 dark:text-gray-200",
										"text-gray-900 dark:text-gray-100",
									)}
									onClick={() => handleSelect(option.value)}
									role="option"
									aria-selected={isSelected}
								>
									{isSelected && (
										<span className="absolute left-2 flex size-3.5 items-center justify-center">
											<Check className="size-4 text-brand-500" />
										</span>
									)}
									<span className="block truncate">{option.text}</span>
								</div>
							);
						})}
					</PopoverContent>
				</Popover>
			</div>
		</div>
	);
};

export default MultiSelect;
